// src/intake/IntakeQuestion.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 23 — Renders a single intake question based on its type
// ─────────────────────────────────────────────────────────────────────────────

import React, { useRef, useEffect } from 'react';
import { IntakeQuestion as IQ } from './questions';
import LoomEmbed from './LoomEmbed';

interface Props {
  question: IQ;
  value: string;
  onChange: (value: string) => void;
  onNext: () => void;
  questionNumber: number;
  totalQuestions: number;
}

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.1)',
  border: '2px solid rgba(255,255,255,0.3)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 20,
  padding: 16,
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
  fontFamily: "'Montserrat', sans-serif",
  minHeight: 56,
  transition: 'border-color 200ms ease',
};

const focusColor = '#64DAD2';

export default function IntakeQuestion({ question, value, onChange, onNext, questionNumber, totalQuestions }: Props) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    // Auto-focus input on mount (not for loom/select)
    if (question.type !== 'loom' && question.type !== 'select') {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [question.id, question.type]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && question.type !== 'textarea') {
      e.preventDefault();
      onNext();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = focusColor;
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
  };

  return (
    <div>
      {/* Question number */}
      <div style={{ color: '#64DAD2', fontSize: 14, fontWeight: 500, marginBottom: 12, fontFamily: "'Montserrat', sans-serif" }}>
        Q{questionNumber} of {totalQuestions}
      </div>

      {/* Question label */}
      <h2 className="intake-label" style={{
        color: '#fff',
        fontSize: 28,
        fontWeight: 600,
        lineHeight: 1.3,
        marginBottom: question.helperText ? 12 : 24,
        fontFamily: "'Montserrat', sans-serif",
      }}>
        {question.label}
      </h2>

      {/* Helper text */}
      {question.helperText && (
        <p style={{
          color: 'rgba(255,255,255,0.7)',
          fontSize: 15,
          fontWeight: 400,
          lineHeight: 1.6,
          marginBottom: 24,
          fontFamily: "'Montserrat', sans-serif",
        }}>
          {question.helperText}
        </p>
      )}

      {/* Loom type */}
      {question.type === 'loom' && question.loomUrl && (
        <div style={{ marginBottom: 16 }}>
          <LoomEmbed url={question.loomUrl} title={question.label} />
          <p style={{
            color: 'rgba(255,255,255,0.5)',
            fontSize: 14,
            marginTop: 16,
            textAlign: 'center',
            fontFamily: "'Montserrat', sans-serif",
          }}>
            Watch the short video above, then click Continue.
          </p>
        </div>
      )}

      {/* Select type — tap-friendly cards */}
      {question.type === 'select' && question.options && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {question.options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                // Auto-advance on select
                setTimeout(onNext, 200);
              }}
              className="intake-select-option"
              style={{
                background: value === opt.value ? 'rgba(100,218,210,0.15)' : 'rgba(255,255,255,0.1)',
                border: `2px solid ${value === opt.value ? '#64DAD2' : 'rgba(255,255,255,0.2)'}`,
                borderRadius: 8,
                padding: '16px 24px',
                cursor: 'pointer',
                color: '#fff',
                fontSize: 18,
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 500,
                textAlign: 'left',
                transition: 'all 150ms ease',
                minHeight: 64,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Text / Email type */}
      {(question.type === 'text' || question.type === 'email') && (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={question.type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={question.placeholder}
          className="intake-input"
          style={{
            ...inputStyle,
          }}
        />
      )}

      {/* Number type */}
      {question.type === 'number' && (
        <div style={{ position: 'relative' }}>
          {question.isCurrency && (
            <span style={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'rgba(255,255,255,0.5)',
              fontSize: 20,
              fontFamily: "'Montserrat', sans-serif",
              pointerEvents: 'none',
            }}>
              £
            </span>
          )}
          {question.isPercentage && (
            <span style={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'rgba(255,255,255,0.5)',
              fontSize: 20,
              fontFamily: "'Montserrat', sans-serif",
              pointerEvents: 'none',
            }}>
              %
            </span>
          )}
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={question.placeholder}
            className="intake-input"
            style={{
              ...inputStyle,
              paddingLeft: question.isCurrency ? 36 : 16,
              paddingRight: question.isPercentage ? 36 : 16,
            }}
          />
        </div>
      )}

      {/* Textarea type */}
      {question.type === 'textarea' && (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleFocus as any}
          onBlur={handleBlur as any}
          placeholder={question.placeholder}
          rows={4}
          className="intake-input"
          style={{
            ...inputStyle,
            resize: 'none',
            minHeight: 120,
          }}
        />
      )}
    </div>
  );
}
